/*
 * DESIGN: Forge & Circuit — Neo-Brutalist Dark Tech
 * Contact: Split layout — form on left, contact details on right.
 * Thick borders, monospace labels, ember CTA button.
 */
import { Phone, Mail, MapPin, Globe, ArrowRight, Send } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import Reveal from "./Reveal";

export default function Contact() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    business: "",
    message: "",
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    toast.success("Message sent! We'll be in touch within 24 hours.");
    setFormData({ name: "", email: "", business: "", message: "" });
  };

  return (
    <section id="contact" className="py-24 md:py-32 relative">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-electric/30 to-transparent" />

      <div className="container">
        {/* Section Header */}
        <div className="mb-16">
          <Reveal
            as="span"
            from="none"
            className="font-mono text-xs text-ember tracking-[0.3em] uppercase block mb-4"
          >
            // GET IN TOUCH
          </Reveal>
          <Reveal
            as="h2"
            from="up"
            duration={0.6}
            className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl text-white mb-4"
          >
            Ready to Put AI
            <br />
            <span className="text-electric">to Work?</span>
          </Reveal>
          <Reveal
            as="p"
            from="up"
            duration={0.6}
            delay={0.1}
            className="text-steel-light text-lg max-w-2xl"
          >
            Whether you run a towing company, an auto shop, or any blue collar business —
            let's talk about how AI can save you time, cut costs, and grow your revenue.
          </Reveal>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Contact Form */}
          <Reveal
            from="up"
            duration={0.6}
            className="lg:col-span-3"
          >
            <form onSubmit={handleSubmit} className="border-[3px] border-border bg-card p-8 md:p-10">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5">
                <div>
                  <label className="font-mono text-[10px] text-steel tracking-[0.2em] uppercase block mb-2">
                    Your Name
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-3 bg-navy border-[2px] border-border text-white font-body text-sm focus:border-electric focus:outline-none transition-colors placeholder:text-steel/50"
                    placeholder="John Smith"
                  />
                </div>
                <div>
                  <label className="font-mono text-[10px] text-steel tracking-[0.2em] uppercase block mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-3 bg-navy border-[2px] border-border text-white font-body text-sm focus:border-electric focus:outline-none transition-colors placeholder:text-steel/50"
                    placeholder="john@company.com"
                  />
                </div>
              </div>

              <div className="mb-5">
                <label className="font-mono text-[10px] text-steel tracking-[0.2em] uppercase block mb-2">
                  Business Type
                </label>
                <input
                  type="text"
                  value={formData.business}
                  onChange={(e) => setFormData({ ...formData, business: e.target.value })}
                  className="w-full px-4 py-3 bg-navy border-[2px] border-border text-white font-body text-sm focus:border-electric focus:outline-none transition-colors placeholder:text-steel/50"
                  placeholder="Towing, Auto Repair, HVAC, etc."
                />
              </div>

              <div className="mb-8">
                <label className="font-mono text-[10px] text-steel tracking-[0.2em] uppercase block mb-2">
                  How Can We Help?
                </label>
                <textarea
                  required
                  rows={5}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="w-full px-4 py-3 bg-navy border-[2px] border-border text-white font-body text-sm focus:border-electric focus:outline-none transition-colors resize-none placeholder:text-steel/50"
                  placeholder="Tell us about your business and what challenges you're facing..."
                />
              </div>

              <button
                type="submit"
                className="inline-flex items-center justify-center gap-2 w-full sm:w-auto px-8 py-4 bg-ember text-white font-heading font-bold text-base border-[3px] border-ember hover:bg-ember/90 transition-all active:translate-y-[1px] group"
              >
                <Send className="w-5 h-5" />
                Send Message
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
            </form>
          </Reveal>

          {/* Contact Info */}
          <Reveal
            from="up"
            duration={0.6}
            delay={0.15}
            className="lg:col-span-2 flex flex-col gap-5"
          >
            {/* Direct Contact */}
            <div className="border-[3px] border-border bg-card p-6">
              <h3 className="font-heading font-bold text-white text-lg mb-5">
                Direct Contact
              </h3>
              <div className="flex flex-col gap-4">
                <a
                  href="tel:614-633-7935"
                  className="flex items-center gap-3 text-steel-light hover:text-white transition-colors group"
                >
                  <div className="w-10 h-10 border-[2px] border-electric/30 bg-electric/10 flex items-center justify-center shrink-0 group-hover:bg-electric/20 transition-colors">
                    <Phone className="w-4 h-4 text-electric" />
                  </div>
                  <div>
                    <div className="font-mono text-[10px] text-steel tracking-[0.2em] uppercase">Phone</div>
                    <div className="text-sm font-medium">614-633-7935</div>
                  </div>
                </a>
                <a
                  href="mailto:chris@bluecollarai.online"
                  className="flex items-center gap-3 text-steel-light hover:text-white transition-colors group"
                >
                  <div className="w-10 h-10 border-[2px] border-electric/30 bg-electric/10 flex items-center justify-center shrink-0 group-hover:bg-electric/20 transition-colors">
                    <Mail className="w-4 h-4 text-electric" />
                  </div>
                  <div>
                    <div className="font-mono text-[10px] text-steel tracking-[0.2em] uppercase">Email</div>
                    <div className="text-sm font-medium">chris@bluecollarai.online</div>
                  </div>
                </a>
                <div className="flex items-center gap-3 text-steel-light">
                  <div className="w-10 h-10 border-[2px] border-electric/30 bg-electric/10 flex items-center justify-center shrink-0">
                    <MapPin className="w-4 h-4 text-electric" />
                  </div>
                  <div>
                    <div className="font-mono text-[10px] text-steel tracking-[0.2em] uppercase">Location</div>
                    <div className="text-sm font-medium">Columbus, OH</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 text-steel-light">
                  <div className="w-10 h-10 border-[2px] border-electric/30 bg-electric/10 flex items-center justify-center shrink-0">
                    <Globe className="w-4 h-4 text-electric" />
                  </div>
                  <div>
                    <div className="font-mono text-[10px] text-steel tracking-[0.2em] uppercase">Service Area</div>
                    <div className="text-sm font-medium">Serving World Wide</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Response */}
            <div className="border-[3px] border-ember/30 bg-ember/5 p-6">
              <div className="flex items-center gap-2 mb-3">
                <span className="w-2 h-2 rounded-full bg-ember animate-pulse" />
                <span className="font-mono text-xs text-ember tracking-wider uppercase">
                  Quick Response
                </span>
              </div>
              <p className="text-steel-light text-sm leading-relaxed">
                We respond to every inquiry within <span className="text-white font-semibold">24 hours</span>.
                For urgent matters, call us directly. We're real people who pick up the phone.
              </p>
            </div>

            {/* Location Map — lazy-loaded so it never blocks initial paint. */}
            <div className="border-[3px] border-border bg-card overflow-hidden">
              <iframe
                title="Blue Collar AI service location — Columbus, OH"
                src="https://www.google.com/maps?q=40.2220891,-82.981049&z=11&output=embed"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="w-full h-56 border-0"
              />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
